import { FAIcon, Theme, SearchDataStructure, AttributeTypes, TagStyle, Tag } from '../../../ui/components';
import { GetColumnsType } from '../../../ui/components/TableContainer/types/propsTypes';
import { DynamicObjectInterface } from '../../../sdk/type';
import { dateTimeToCustomFormat } from '../../../ui/utils';

type IProductBO = {
  [key: string]: DynamicObjectInterface;
};

export interface TeamData {
  teamId: number | null;
  teamName: string;
  teamSize?: string;
  description?: string;
  campaignId: string;
  campaignRecId: number | null;
  campaignName: string;
  campaignStart: string;
  campaignEnd: string;
  teamLeader?: string;
  coachId?: string;
  coachName?: string;
  teamStartDate: string;
  teamEndDate: string;
  status: string;
  participationType: string;
}

export interface ParticipantDataStructure {
  key?: number;
  campaignId: string;
  teamId: number;
  name: string;
  userId: string;
  status: string;
  recId?: number;
  weightIn: {
    weightInDate: string | null;
    height: number;
    acknowledgeBy: string | null;
    acknowledgeDateTime: string | null;
    remarks: string | null;
  };
  weightOut: {
    weightOutDate: string | null;
    height: number;
    acknowledgeBy: string | null;
    acknowledgeDateTime: string | null;
    remarks: string | null;
  };
  purchaseDate: string | null;
  purchaseData: {
    orderNumber: number | null;
    orderPoint: number | null;
  };
  isIndividualAchieved?: string;
  loginId?: string;
  orderStatus?: string;
  weightInDate?: string;
  weightOutDate?: string;
}
export interface CreateTeamData {
  teamId: number | null;
  teamName: string;
  description?: string;
  campaignId: string;
  teamLeader?: string;
  coachId?: string;
  userId?: string;
  teamStartDate: string;
  teamEndDate: string;
  status: string;
  remark?: string;
  participant?: string[];
  checkbox?: string;
}

export interface CampaignOption {
  campaignEnd: string;
  campaignId: string;
  campaignName: string;
  campaignStart: string;
  status: string;
  title: string;
  value: string;
}

export interface AddParticipantStructure {
  campaignId: string;
  userId?: number;
  loginId: string;
  coachId: string;
  enrollmentDate: string;
  endDate: string;
  pin: string;
  remarks: string;
  orderNumber: string;
  directLeaderId: number;
  directLeaderName: string;
  directLeaderPinLevel: number;
  isNewAbo?: boolean;
  witnessName?: string;
  witnessContact?: string;
  joinedHealthyWarrior?: boolean;
  invisibleParticipant: {
    name: string;
    email: string;
    phone: string;
  };
  healthGoals: {
    title: string;
    key: string;
  }[];
  rewardCode?: string;
}

export interface SearchedUserData {
  userId?: number;
  name: string;
  email: string;
  phone: string;
  status?: string;
  pinLevel: string | number;
  userPinName?: string;
  directLeaderId?: number;
  directLeaderName: string;
  directLeaderPinLevel: number;
  directLeaderPinName?: string;
  invisibleParticipant?: {
    name: string;
    email: string;
    phoneNumber: string;
  };
  isNewAbo?: boolean;
  gender?: string;
  race?: string;
  address?: string;
  loaName?: string;
  accountType?: string;
  highestPinLevel?: string | number;
  partyId?: string | number;
}

const dataSearchOption: SearchDataStructure[] = [
  // {
  //   name: 'Campaign Name',
  //   key: 'campaignName',
  //   type: AttributeTypes.Textbox,
  //   visibility: true,
  // },
  {
    name: 'Team Name',
    key: 'teamName',
    type: AttributeTypes.Textbox,
    visibility: true,
  },
  {
    name: 'Team Leader ID',
    key: 'coachId',
    type: AttributeTypes.Textbox,
    visibility: true,
  },
  {
    name: 'Team Winning',
    key: 'isGroupAchieved',
    type: AttributeTypes.DropdownSingleSelect,
    visibility: true,
    dataset: [
      {
        key: 'yes',
        value: 'YES',
      },
      {
        key: 'no',
        value: 'NO',
      },
      {
        key: 'pending',
        value: 'PENDING',
      },
    ],
  },
  {
    name: 'Status',
    key: 'status',
    type: AttributeTypes.DropdownSingleSelect,
    visibility: false,
    dataset: [
      {
        key: 'true',
        value: 'ACTIVE',
      },
      {
        key: 'false',
        value: 'INACTIVE',
      },
    ],
  },
  {
    name: 'Start Date',
    key: 'teamStartDate',
    type: AttributeTypes.DateTimeSingle,
    visibility: false,
  },
  {
    name: 'End Date',
    key: 'teamEndDate',
    type: AttributeTypes.DateTimeSingle,
    visibility: false,
  },
];

const getTeamListConfig: GetColumnsType = (styles: Object, translation: Function, cb: DynamicObjectInterface) => [
  // {
  //   key: 'campaignName',
  //   text: translation('admin.teams.campaignName'),
  //   sortable: true,
  //   width: 5,
  //   render: (object: IProductBO) => <span>{object.campaignName}</span>,
  // },
  {
    key: 'teamName',
    text: translation('admin.teams.teamName'),
    sortable: true,
    width: 4,
    render: (object: IProductBO) => <span>{object.teamName}</span>,
  },
  {
    key: 'teamStartDate',
    text: translation('admin.teams.period'),
    sortable: true,
    width: 5,
    render: (object: IProductBO) => (
      <span>
        {object.teamStartDate !== null && object.teamEndDate !== null
          ? `${dateTimeToCustomFormat(object.teamStartDate)} - ${dateTimeToCustomFormat(object.teamEndDate)}`
          : '-'}
      </span>
    ),
  },
  {
    key: 'coachId',
    text: translation('admin.teams.coachId'),
    sortable: true,
    width: 4,
    render: (object: IProductBO) => <span>{object.coachId !== '' ? object.coachId : '-'}</span>,
  },
  {
    key: 'teamId',
    text: translation('admin.teams.teamId'),
    sortable: true,
    width: 4,
    render: (object: IProductBO) => <span>{object.teamId !== '' ? object.teamId : '-'}</span>,
  },
  {
    key: 'teamSize',
    text: translation('admin.teams.teamSize'),
    sortable: true,
    width: 4,
    render: (object: IProductBO) => <span>{object.teamSize !== '' ? object.teamSize : '-'}</span>,
  },
  // {
  //   key: 'isMeasured',
  //   text: translation('admin.teams.isMeasured'),
  //   sortable: true,
  //   width: 3,
  //   render: (object: IProductBO) => (
  //     <span>
  //       <Tag
  //       theme={
  //         object.isMeasured === 1
  //           ? Theme.SUCCESS
  //           : Theme.WARNING
  //       }
  //       style={TagStyle.OUTLINE}
  //       text={object.isMeasured === 1 ? 'YES' : 'NO'}
  //     />
  //     </span>
  //   ),
  // },
  {
    key: 'isGroupAchieved',
    text: translation('admin.teams.isGroupAchieved'),
    sortable: true,
    width: 3,
    render: (object: IProductBO) => (
      <span>
        <Tag
          theme={
            object.isGroupAchieved === 'YES' ? Theme.SUCCESS : object.isGroupAchieved === 'NO' ? Theme.WARNING : Theme.BLUE
          }
          style={TagStyle.OUTLINE}
          text={object.isGroupAchieved}
        />
      </span>
    ),
  },
  {
    key: 'newABO',
    text: translation('admin.participant.isNewAbo'),
    sortable: true,
    width: 3,
    render: (object: IProductBO) => <span>{object.newABO}</span>,
  },
  // {
  //   key: 'isGroupWinning',
  //   text: translation('admin.teams.isGroupWinning'),
  //   sortable: true,
  //   width: 3,
  //   render: (object: IProductBO) => (
  //     <span>
  //       <Tag
  //         theme={
  //           object.isGroupWinning === 'YES'
  //             ? Theme.SUCCESS
  //             : object.isGroupWinning === 'NO'
  //               ? Theme.WARNING
  //               : Theme.BLUE
  //         }
  //         style={TagStyle.OUTLINE}
  //         text={object.isGroupWinning}
  //       />
  //       {/* <Tag
  //         theme={
  //           object.isGroupAchieved === 1 && object.newABO > 0 ? Theme.SUCCESS
  //           : object.isGroupAchieved === 0 || (object.newABO === 0 && object.isGroupAchieved === 1)
  //           ? Theme.WARNING : Theme.BLUE
  //         }
  //         style={TagStyle.OUTLINE}
  //         text={object.isGroupAchieved === 1 && object.newABO > 0 ? 'YES'
  //           : object.isGroupAchieved === 0 || (object.newABO === 0 && object.isGroupAchieved === 1)
  //           ? 'NO' : 'PENDING'}
  //       /> */}
  //     </span>
  //   ),
  // },
  {
    key: 'status',
    text: translation('admin.teams.status'),
    sortable: true,
    width: 3,
    render: (object: IProductBO) => (
      <span>
        <Tag
          theme={object.status === 'ACTIVE' ? Theme.SUCCESS : Theme.WARNING}
          style={TagStyle.OUTLINE}
          text={object.status}
        />
      </span>
    ),
  },
  {
    key: 'action',
    width: 2,
    text: 'Action',
    render: (object: IProductBO) => (
      <>
        <FAIcon
          iconName={'edit'}
          onClick={e => {
            cb?.onEdit(object.teamId);
            e.stopPropagation();
          }}
          theme={Theme.PURPLE}
          title={'Edit'}
        />
        {' | '}
        <FAIcon
          iconName={'trash-can'}
          onClick={e => {
            cb?.onDelete(object);
            e.stopPropagation();
          }}
          theme={Theme.PURPLE}
          title={'Delete'}
        />
      </>
    ),
  },
];

export { getTeamListConfig, dataSearchOption };
